import type { ActionTree, ActionContext, Module, MutationTree } from 'vuex';
import type { AxiosResponse } from 'axios';
import { ltLuxon } from '../../helpers/ltLuxon';
import { ClubsModuleConstant, StorageConstant } from '../../constants';
import { clubService } from '../../services/clubService';
import { logger } from '@/lib/helpers/logger';
import { storageService } from '@life-time-inc/enterprise-framework-core';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

interface IClubsState {
	clubsList: ltFramework.IClubsList;
}

const ClubsState: IClubsState = {
	clubsList: {
		clubs: [],
		initialized: false,
		lastUpdate: ''
	}
};

const { action, mutation } = ClubsModuleConstant;

const ClubsMutations: MutationTree<IClubsState> = {
	[mutation.setClubs](state: IClubsState, payload: ltFramework.IClubsList) {
		state.clubsList = payload;
	}
};

export const ClubsActions: ActionTree<IClubsState, ltFramework.IRootState> = {
	async [action.initialize]({
		commit
	}: ActionContext<IClubsState, ltFramework.IRootState>): Promise<void> {
		const storageData: ltFramework.IClubsList = storageService.localGet(
			StorageConstant.clubs
		) as ltFramework.IClubsList;
		let lastUpdate = '';
		try {
			const clubsHeaderResponse: AxiosResponse = await clubService.getClubsHeader();
			lastUpdate = clubsHeaderResponse.headers['last-modified'] ?? '';
		}
		catch (e: unknown) {
			logger.error('Clubs Module Initialize: Failed clubs HEAD', {}, e);
		}
		const storageDataLoaded = storageData?.clubs && storageData.clubs.length > 0;
		if (storageDataLoaded && ltLuxon(storageData.lastUpdate) >= ltLuxon(lastUpdate)) {
			const storageClubsList: ltFramework.IClubsList = {
				clubs: storageData.clubs,
				initialized: true,
				lastUpdate: storageData.lastUpdate
			};

			commit(mutation.setClubs, storageClubsList);
		}
		else {
			storageService.localRemove(StorageConstant.clubs);
			let clubs: ltFramework.IClub[] = [];
			try {
				const response: AxiosResponse<ltFramework.IClub[]> = await clubService.getClubs();
				clubs = response.data;
			}
			catch (e: unknown) {
				logger.error('Clubs Module Initialize: Failed clubs GET', {}, e);
			}

			clubs = clubs.map((club) => {
				return {
					...club,
					membershipGroupsAndItems: {
						groups: {},
						items: {}
					}
				};
			});
			const clubsList: ltFramework.IClubsList = {
				clubs,
				initialized: true,
				lastUpdate: lastUpdate
			};
			storageService.localSet(StorageConstant.clubs, clubsList);

			commit(mutation.setClubs, clubsList);
		}
	}
};

export const ClubsModule: Module<IClubsState, ltFramework.IRootState> = {
	namespaced: true,
	state: ClubsState,
	actions: ClubsActions,
	mutations: ClubsMutations
};
